import { v4 as uuid } from 'uuid';
import * as BlipSdk from 'blip-sdk';
import * as WebSocketTransport from 'lime-transport-websocket';
import Semaphore from 'semaphore-async-await';
import { LimeError } from 'modules/application/misc/LimeError';
import { emitter } from 'data/function';
import { MessagingHubFeatures } from './MessagingHubFeatures';
const MAX_CONNECT_USER_TRY_COUNT = 6;
export const CONNECT_USER_ERROR = 'Connect user error';
export const BlipServiceReadyEvent = 'onBlipServiceReady';
export class BlipService {
    static $inject = ['BLIP_DOMAIN', 'BLIP_ACCOUNT_ISSUER', 'BLIP_AZURE_ISSUER', 'BLIP_WEBSOCKET_HOSTNAME', 'BLIP_WEBSOCKET_HOSTNAME_TENANT', 'BLIP_WEBSOCKET_PORT', 'BLIP_WEBSOCKET_SCHEME', 'AuthenticationService', '$state', '$timeout', 'UrlTenantHandlerService', 'COMMAND_TIMEOUT', 'BLIP_DOMAIN_URL'];
    constructor(BLIP_DOMAIN, BLIP_ACCOUNT_ISSUER, BLIP_AZURE_ISSUER, BLIP_WEBSOCKET_HOSTNAME, BLIP_WEBSOCKET_HOSTNAME_TENANT, BLIP_WEBSOCKET_PORT, BLIP_WEBSOCKET_SCHEME, AuthenticationService, $state, $timeout, UrlTenantHandlerService, COMMAND_TIMEOUT, BLIP_DOMAIN_URL) {
        'ngInject';
        this.BLIP_DOMAIN = BLIP_DOMAIN;
        this.BLIP_ACCOUNT_ISSUER = BLIP_ACCOUNT_ISSUER;
        this.BLIP_AZURE_ISSUER = BLIP_AZURE_ISSUER;
        this.BLIP_WEBSOCKET_HOSTNAME = BLIP_WEBSOCKET_HOSTNAME;
        this.BLIP_WEBSOCKET_HOSTNAME_TENANT = BLIP_WEBSOCKET_HOSTNAME_TENANT;
        this.BLIP_WEBSOCKET_PORT = BLIP_WEBSOCKET_PORT;
        this.BLIP_WEBSOCKET_SCHEME = BLIP_WEBSOCKET_SCHEME;
        this.AuthenticationService = AuthenticationService;
        this.$state = $state;
        this.$timeout = $timeout;
        this.UrlTenantHandlerService = UrlTenantHandlerService;
        this.COMMAND_TIMEOUT = COMMAND_TIMEOUT;
        this.BLIP_DOMAIN_URL = BLIP_DOMAIN_URL;
        this.lock = new Semaphore(1);
        this.init();
    }
    async init() {
        this.connectionTryCount = 0;
        const authData = this.AuthenticationService.getAuth();
        const issuer = authData?.issuer;
        this.AZURE_AD_ENABLED = issuer?.includes('login.blip.ai');
        this.createInitializationPromise();
    }
    createInitializationPromise() {
        this.waitForInitializationPromise = {
            resolve: () => undefined,
            reject: () => undefined,
            promise: new Promise(() => undefined),
        };
        const promise = new Promise((resolve, reject) => {
            // @ts-ignore
            this.waitForInitializationPromise.resolve = resolve;
            this.waitForInitializationPromise.reject = reject;
        });
        this.waitForInitializationPromise.promise = promise;
    }
    waitForInitialization() {
        return this.waitForInitializationPromise.promise;
    }
    async sendMessage(message) {
        await this.client.sendMessage(message);
    }
    async sendNotification(message) {
        await this.client.sendNotification(message);
    }
    /**
     * Send command and return just it resource
     * @param command Lime.Command
     * @param timeout Timeout until reject command
     * @deprecated USE processCommand instead!
     */
    async sendCommand(command, timeout = this.COMMAND_TIMEOUT) {
        try {
            if (this.isConnected()) {
                const requestCommand = {
                    ...command,
                    id: command.id ? command.id : uuid(),
                };
                this.injectAuthenticationMetadata(requestCommand);
                this.injectSaveCommandMetadata(requestCommand);
                await this.injectSaveCommandMetadataForDelegations(requestCommand);
                const { resource } = await this.client.sendCommand(requestCommand, timeout);
                return resource;
            }
            else {
                throw new Error('Blip Service is not connected, try to connect again');
            }
        }
        catch (e) {
            try {
                const error = JSON.parse(e);
                if (error.reason) {
                    throw new LimeError(error.reason);
                }
            }
            catch (err) {
                throw e;
            }
        }
    }
    /**
     * This method pass full responsability to lime-js library to use transport to send commands.
     * It will never reject promises and always resolve them. The responsability to reject or
     * throw errors is fully of project that will use blip-sdk
     * @param command Lime.Command
     * @param timeout Timeout until reject command
     */
    async processCommand(command, timeout = this.COMMAND_TIMEOUT) {
        const requestCommand = {
            ...command,
            id: command.id ? command.id : uuid(),
        };
        this.injectAuthenticationMetadata(requestCommand);
        this.injectSaveCommandMetadata(requestCommand);
        await this.injectSaveCommandMetadataForDelegations(requestCommand);
        return await this.client.processCommand(requestCommand, timeout);
    }
    /**
     * Return a command response or throw an exception if it failed.
     * @param response Command response, including status and resource/reason when available
     */
    ensureCommandSuccess(response) {
        if (response.status == 'success') {
            return response;
        }
        const { code, description } = response.reason;
        throw new LimeError({ code, description });
    }
    isConnected() {
        return this.client;
    }
    /**
     * This method returns a resolved promise when client is connected
     */
    onConnect(fn) {
        if (!this.client) {
            // Its become necessary due to be an external event thats not handled by angular lifecycle
            emitter.on(BlipServiceReadyEvent, () => this.$timeout(fn));
        }
        else {
            fn();
        }
    }
    async connect(identifier, token) {
        if (this.client) {
            return;
        }
        try {
            await this.lock.wait();
            if (this.client) {
                return;
            }
            if (identifier === 'undefined') {
                console.error('Identifier is empty, try to connect again');
                return;
            }
            this.client = await this.connectWithExponentialBackoff(identifier, token);
            this.waitForInitializationPromise.resolve();
            emitter.emit(BlipServiceReadyEvent);
        }
        catch (e) {
            console.error(e);
        }
        finally {
            this.lock.signal();
        }
    }
    async connectWithExponentialBackoff(identifier, token) {
        let client;
        if (this.connectionTryCount >= MAX_CONNECT_USER_TRY_COUNT) {
            const error = new Error(`${CONNECT_USER_ERROR}: Could not connect user ${identifier} - Max connection try count of ${MAX_CONNECT_USER_TRY_COUNT} reached. Please refresh the page.`);
            this.$state.go('applicationError', { error });
            throw error;
        }
        this.connectionTryCount++;
        if (this.AuthenticationService.auth && this.AuthenticationService.auth.tokenType !== 'Bearer') {
            this.$state.go('anon.login');
        }
        try {
            client = this.buildBlipClient(identifier, token, this.BLIP_WEBSOCKET_HOSTNAME);
            await client.connect();
            const tenantPrefix = this.UrlTenantHandlerService.getTenantPrefixFromUrl();
            if (tenantPrefix) {
                this.isOrganizationEnabled = await this.checkOrganizationEnabled(tenantPrefix, identifier, client);
                if (this.isOrganizationEnabled) {
                    client.close();
                    client = this.createClientWithTenantHostname(identifier, token);
                    await client.connect();
                }
            }
            this.connectionTryCount = 0;
            return client;
        }
        catch (e) {
            console.error(e);
            client._transport.onClose = () => { };
            client.close();
            // Use an exponential backoff for the timeout
            const timeout = 100 * Math.pow(2, this.connectionTryCount);
            return new Promise((resolve, reject) => {
                // try to reconnect after the timeout
                setTimeout(async () => {
                    const client = await this.connectWithExponentialBackoff(identifier, token);
                    resolve(client);
                }, timeout);
            });
        }
    }
    async close() {
        if (!this.client) {
            return;
        }
        try {
            await this.lock.wait();
            if (!this.client) {
                return;
            }
            await this.client.close();
            this.client = undefined;
        }
        finally {
            this.lock.signal();
        }
    }
    addMessageReceiver(receiver, filter) {
        if (this.client) {
            return this.client.addMessageReceiver(filter, receiver);
        }
    }
    get localNode() {
        return this.client._clientChannel.localNode;
    }
    get remoteNode() {
        return this.client._clientChannel.remoteNode;
    }
    get sessionId() {
        return this.client._clientChannel.sessionId;
    }
    get Media() {
        return this.client.Media;
    }
    createClientWithTenantHostname(identifier, token) {
        const blipWebsocketHostname = this.UrlTenantHandlerService.addTenantPrefixToUrl(this.BLIP_WEBSOCKET_HOSTNAME_TENANT, undefined, this.BLIP_WEBSOCKET_HOSTNAME);
        return this.buildBlipClient(identifier, token, blipWebsocketHostname);
    }
    buildBlipClient(identifier, token, hostname) {
        return new BlipSdk.ClientBuilder()
            .withIdentifier(identifier)
            .withToken(token)
            .withDomain(this.BLIP_DOMAIN)
            .withIssuer(this.AZURE_AD_ENABLED ? this.BLIP_AZURE_ISSUER : this.BLIP_ACCOUNT_ISSUER)
            .withHostName(hostname)
            .withPort(this.BLIP_WEBSOCKET_PORT)
            .withScheme(this.BLIP_WEBSOCKET_SCHEME)
            .withRoutingRule('identity') // To avoid conflicts with BLiP Desk
            .withInstance('portal')
            .withNotifyConsumed(false)
            .withTransportFactory(() => new WebSocketTransport())
            .build();
    }
    injectSaveCommandMetadata(requestCommand) {
        requestCommand.metadata = requestCommand.metadata || {};
        if (requestCommand.method === 'set' ||
            requestCommand.method === 'delete' ||
            requestCommand.method === 'merge') {
            requestCommand.metadata['server.shouldStore'] = 'true';
        }
    }
    async injectSaveCommandMetadataForDelegations(requestCommand) {
        const isDelegationCommand = (requestCommand) => requestCommand.pp || requestCommand.uri.startsWith('lime://');
        if (requestCommand.method === 'get' &&
            isDelegationCommand(requestCommand) &&
            await MessagingHubFeatures.enableGetDelegationCommandsLogging()) {
            requestCommand.metadata = requestCommand.metadata || {};
            requestCommand.metadata['server.shouldStore'] = 'true';
        }
    }
    injectAuthenticationMetadata(requestCommand) {
        requestCommand.metadata = requestCommand.metadata || {};
        requestCommand.metadata['blip_portal.email'] = this.AuthenticationService.email;
    }
    async checkOrganizationEnabled(tenantPrefix, identifier, client) {
        try {
            await client.sendCommand({
                id: uuid(),
                method: 'get',
                to: `postmaster@portal.${this.BLIP_DOMAIN}`,
                uri: `/tenants/${tenantPrefix}/users/${encodeURIComponent(identifier)}@blip.ai`,
            });
            return true;
        }
        catch (err) {
            console.error(err);
            return false;
        }
    }
}
BlipService.$inject = ['BLIP_DOMAIN', 'BLIP_ACCOUNT_ISSUER', 'BLIP_AZURE_ISSUER', 'BLIP_WEBSOCKET_HOSTNAME', 'BLIP_WEBSOCKET_HOSTNAME_TENANT', 'BLIP_WEBSOCKET_PORT', 'BLIP_WEBSOCKET_SCHEME', 'AuthenticationService', '$state', '$timeout', 'UrlTenantHandlerService', 'COMMAND_TIMEOUT', 'BLIP_DOMAIN_URL'];
